import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, AlertTriangle, Shield, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Navigation */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-home-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png" 
                  alt="RouteFlow AI" 
                  className="h-10 w-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card className="border-0 shadow-lg" data-testid="privacy-policy-card">
          <CardHeader>
            <CardTitle className="text-3xl">Privacy Policy</CardTitle>
            <p className="text-gray-600">Last updated: March 2025</p>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-6">
            
            {/* IMPORTANT LEGAL NOTICE */}
            <section className="bg-amber-50 border-2 border-amber-500 p-6 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-bold mb-3 text-amber-800">IMPORTANT: DATA PROCESSING DISCLOSURE</h2>
                  <p className="text-gray-700 mb-2">
                    <strong>RouteFlow AI, LLC is a technology provider only.</strong> We provide a software interface and do not process financial transactions. 
                    When you use our service to initiate transfers, your personal and financial data is collected and processed directly by 
                    <strong> Guardarian OÜ</strong>, our licensed financial services partner.
                  </p>
                  <p className="text-gray-700">
                    Please review Guardarian OÜ's Privacy Policy at 
                    <a href="https://guardarian.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline font-semibold"> https://guardarian.com/privacy-policy</a> 
                    for information about how your financial data is handled.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
              <p className="text-gray-700">
                Welcome to RouteFlow AI. We are committed to protecting your personal information and your right to privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our technology platform.
              </p>
              <p className="text-gray-700 mt-3">
                <strong>Important:</strong> RouteFlow AI is a technology provider and software interface only. We are not a financial services provider, 
                money services business, or money transmitter. All financial services, including data collection for KYC/AML compliance, 
                are provided by our partner Guardarian OÜ.
              </p>
            </section>

            {/* Technology Provider Disclosure */}
            <section className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-semibold mb-2 text-blue-800">2. Technology Provider Disclosure</h2>
                  <p className="text-gray-700 mb-2">
                    RouteFlow AI, LLC operates solely as a <strong>technology provider</strong>. We:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
                    <li>Do NOT collect identity verification documents (KYC data)</li>
                    <li>Do NOT process financial transactions</li>
                    <li>Do NOT store banking or payment card information</li>
                    <li>Do NOT have access to funds or cryptocurrency</li>
                  </ul>
                  <p className="text-gray-700 mt-3">
                    All financial data processing, including identity verification and transaction processing, is handled exclusively by 
                    <strong> Guardarian OÜ</strong> under their privacy policy and data protection practices.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">3. Information We Collect</h2>
              <p className="text-gray-700 mb-2">RouteFlow AI collects limited information for platform functionality:</p>
              
              <h3 className="text-lg font-semibold mt-4 mb-2">3.1 Account Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Email address (for account creation and communication)</li>
                <li>Full name (for account identification)</li>
                <li>Password (encrypted, for account access)</li>
              </ul>

              <h3 className="text-lg font-semibold mt-4 mb-2">3.2 Usage Data</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>AI chat interaction logs (to improve service)</li>
                <li>Voice assistant recordings (temporarily processed for transcription, not stored)</li>
                <li>Route preference history (to optimize suggestions)</li>
                <li>Device and browser information (for security and compatibility)</li>
                <li>IP address (for security and fraud prevention)</li>
              </ul>

              <h3 className="text-lg font-semibold mt-4 mb-2">3.3 Information We Do NOT Collect</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Government-issued identification documents</li>
                <li>Bank account numbers or details</li>
                <li>Credit/debit card numbers</li>
                <li>Social Security numbers or tax identification numbers</li>
                <li>Proof of address documents</li>
                <li>Cryptocurrency wallet private keys</li>
              </ul>
              <p className="text-gray-700 mt-2 bg-blue-50 p-3 rounded">
                <strong>Note:</strong> The above information is collected directly by Guardarian OÜ when you initiate a transaction. 
                RouteFlow AI does not have access to this data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">4. How We Use Your Information</h2>
              <p className="text-gray-700 mb-2">We use the information we collect to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Provide, maintain, and improve our technology platform</li>
                <li>Facilitate communication between you and Guardarian OÜ</li>
                <li>Provide AI-powered route optimization and suggestions</li>
                <li>Send service notifications and updates</li>
                <li>Detect and prevent fraud and abuse</li>
                <li>Respond to customer support inquiries</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            {/* Third-Party Data Processing */}
            <section className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
              <div className="flex items-start gap-3">
                <Database className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-semibold mb-2 text-purple-800">5. Third-Party Data Processing</h2>
                  <p className="text-gray-700 mb-3">
                    When you use RouteFlow AI to initiate a money transfer, you will be redirected to Guardarian OÜ's platform where:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                    <li><strong>Guardarian OÜ collects your identity verification documents</strong> (passport, ID, proof of address) for KYC compliance</li>
                    <li><strong>Guardarian OÜ processes your payment information</strong> (card details, bank information)</li>
                    <li><strong>Guardarian OÜ executes the financial transaction</strong> and handles all regulatory compliance</li>
                  </ul>
                  <p className="text-gray-700 mt-3">
                    RouteFlow AI does not receive, store, or have access to the personal financial data you provide to Guardarian OÜ. 
                    Please refer to <a href="https://guardarian.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:underline font-semibold">Guardarian's Privacy Policy</a> for 
                    details on their data handling practices.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">6. Non-Custodial Platform</h2>
              <p className="text-gray-700">
                RouteFlow AI operates on a non-custodial basis. We do not store or have access to your private keys, wallet seeds, or funds. 
                You maintain full control over your financial accounts at all times. We cannot access, move, freeze, or recover your funds under any circumstances.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">7. Voice Assistant Data Processing</h2>
              <p className="text-gray-700 mb-2">
                When you use our voice assistant feature:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Voice recordings are temporarily processed by OpenAI's Whisper service for transcription</li>
                <li>We do not permanently store voice recordings</li>
                <li>Transcribed text may be retained as part of your chat history to improve service</li>
                <li>You can disable voice features at any time</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Please refer to <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">OpenAI's Privacy Policy</a> for 
                details on their data handling practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">8. Gas Fee Sponsorship</h2>
              <p className="text-gray-700 mb-2">
                To enhance user experience, RouteFlow AI sponsors blockchain gas fees for recipient withdrawals:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>We maintain a platform wallet to cover network transaction fees (gas) on your behalf</li>
                <li>This wallet is used solely to send small amounts of cryptocurrency (POL) to cover gas fees</li>
                <li>We do not have access to your funds - only the ability to send gas to facilitate your withdrawal</li>
                <li>No personal data is collected or stored as part of this gas sponsorship process</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">9. Data Security</h2>
              <p className="text-gray-700">
                We implement appropriate technical and organizational security measures to protect your personal information, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mt-2">
                <li>256-bit SSL/TLS encryption for data in transit</li>
                <li>Encrypted password storage using industry-standard hashing</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication requirements</li>
              </ul>
              <p className="text-gray-700 mt-3">
                However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">10. Information Sharing</h2>
              <p className="text-gray-700 mb-2">
                We do not sell your personal information. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li><strong>With Guardarian OÜ:</strong> To facilitate your transaction requests (email for customer communication)</li>
                <li><strong>Service Providers:</strong> With vendors who help us operate our platform (hosting, analytics)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">11. Your Rights</h2>
              <p className="text-gray-700 mb-2">Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Access and receive a copy of your personal data</li>
                <li>Rectify inaccurate personal data</li>
                <li>Request deletion of your personal data</li>
                <li>Object to processing of your personal data</li>
                <li>Withdraw consent at any time</li>
                <li>Data portability</li>
                <li>Lodge a complaint with a supervisory authority</li>
              </ul>
              <p className="text-gray-700 mt-3">
                <strong>For data held by Guardarian OÜ:</strong> You must contact Guardarian directly to exercise your rights regarding 
                financial and KYC data they have collected.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">12. Cookies and Tracking</h2>
              <p className="text-gray-700">
                We use cookies and similar tracking technologies to track activity on our platform and store certain information. 
                These are essential for platform functionality, security, and analytics. 
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">13. Data Retention</h2>
              <p className="text-gray-700">
                We retain your account information for as long as your account is active or as needed to provide you services. 
                We may retain certain information as required by law or for legitimate business purposes. 
                You may request deletion of your account at any time by contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">14. International Data Transfers</h2>
              <p className="text-gray-700">
                Your information may be transferred to and processed in countries other than your country of residence. 
                These countries may have different data protection laws. We take appropriate safeguards to ensure your data 
                remains protected in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">15. Children's Privacy</h2>
              <p className="text-gray-700">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. 
                If we become aware that we have collected data from a child, we will take steps to delete it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">16. Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page 
                and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">17. Contact Us</h2>
              <p className="text-gray-700">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="text-gray-700 mt-3 space-y-1">
                <p className="font-semibold">RouteFlow AI, LLC</p>
                <p>131 Continental Dr, Suite 305</p>
                <p>Newark, DE 19713, United States</p>
                <p className="mt-2">Privacy Inquiries: <a href="mailto:privacy@routeflowai.com" className="text-emerald-600 hover:underline">privacy@routeflowai.com</a></p>
                <p>General Inquiries: <a href="mailto:info@routeflowai.com" className="text-emerald-600 hover:underline">info@routeflowai.com</a></p>
                <p>Phone: <a href="tel:+13025202688" className="text-emerald-600 hover:underline">+1 (302) 520-2688</a></p>
              </div>
            </section>

            {/* Guardarian Contact */}
            <section className="bg-gray-100 border-2 border-gray-300 p-4 rounded-lg">
              <h2 className="text-lg font-semibold mb-2">For Financial Data Inquiries</h2>
              <p className="text-gray-700">
                For questions about personal data collected during financial transactions (KYC documents, payment information), 
                please contact Guardarian OÜ directly:
              </p>
              <div className="text-gray-700 mt-2">
                <p><strong>Guardarian OÜ</strong></p>
                <p>Website: <a href="https://guardarian.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">https://guardarian.com</a></p>
                <p>Privacy Policy: <a href="https://guardarian.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">https://guardarian.com/privacy-policy</a></p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
